<template>
  <EditorLayout
    class="flsa-skeleton-loader-container"
    :side-by-side-view="false"
  >
    <template #left>
      <div class="flsa-steps-sidebar pa-8">
        <v-skeleton-loader
          type="heading"
          width="180px"
          class="mb-2 skeleton-nav-title"
          :class="{ show: animationIndex > 1, hide: animationIndex <= 1 }"
        />

        <v-skeleton-loader
          type="heading"
          width="120px"
          class="mb-1 skeleton-nav-row"
          :class="{ show: animationIndex > 1, hide: animationIndex <= 1 }"
        />

        <v-skeleton-loader
          type="heading"
          width="100px"
          class="flsa-exemptions-heading-skel skeleton-sub-heading"
          :class="{ show: animationIndex > 1, hide: animationIndex <= 1 }"
        />

        <div
          v-for="i in exemptionSkeletonCount"
          :key="i"
          class="flsa-nav-child-skel"
          :class="{ show: animationIndex > 2, hide: animationIndex <= 2 }"
        >
          <v-skeleton-loader
            type="heading"
            width="160px"
            class="skeleton-sub-heading"
          />
        </div>

        <v-skeleton-loader
          type="heading"
          width="100px"
          class="mt-2 skeleton-nav-row"
          :class="{ show: animationIndex > 2, hide: animationIndex <= 2 }"
        />
      </div>
    </template>

    <template #content>
      <div class="flsa-skeleton-content w-100">
        <div class="flsa-header-block px-8 pt-8 pb-4">
          <div class="d-flex flex-column ga-2">
            <v-skeleton-loader
              type="heading"
              width="65%"
              class="skeleton-page-title"
              :class="{ show: animationIndex > 1, hide: animationIndex <= 1 }"
            />
            <v-skeleton-loader
              type="text"
              width="40%"
              class="skeleton-meta"
              :class="{ show: animationIndex > 1, hide: animationIndex <= 1 }"
            />
          </div>
        </div>

        <div class="flsa-body px-8 pb-8 pt-4">
          <div class="flsa-content-rail">
            <v-skeleton-loader
              type="heading"
              width="55%"
              class="mb-1 skeleton-section-title"
              :class="{ show: animationIndex > 2, hide: animationIndex <= 2 }"
            />
            <v-skeleton-loader
              type="paragraph"
              width="100%"
              class="mb-4"
              :class="{ show: animationIndex > 2, hide: animationIndex <= 2 }"
            />

            <div
              v-for="i in questionRowCount"
              :key="i"
              class="flsa-question-row-skel mb-4"
              :class="{ show: animationIndex > 3, hide: animationIndex <= 3 }"
            >
              <v-skeleton-loader
                type="text"
                width="100%"
                class="mb-1"
              />
              <v-skeleton-loader
                type="text"
                width="85%"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #right>
      <div class="flsa-right-sidebar-skel">
        <div class="flsa-right-sidebar-header px-6 py-4">
          <v-skeleton-loader
            type="heading"
            width="120px"
            class="skeleton-right-title"
            :class="{ show: animationIndex > 1, hide: animationIndex <= 1 }"
          />
        </div>

        <div class="flsa-right-sidebar-body px-6 pt-4 pb-6">
          <div
            v-for="i in rightSidebarRowCount"
            :key="i"
            class="flsa-right-comment-row mb-4"
            :class="{ show: animationIndex > 2, hide: animationIndex <= 2 }"
          >
            <v-skeleton-loader
              type="text"
              width="100%"
              class="mb-1"
            />
            <v-skeleton-loader
              type="text"
              :width="rightSidebarSecondLineWidth(i)"
            />
          </div>
        </div>
      </div>
    </template>
  </EditorLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import EditorLayout from '@/components/editor/EditorLayout.vue';

const exemptionSkeletonCount = 7;
const questionRowCount = 4;
const rightSidebarRowCount = 4;

const rightSidebarSecondLineWidth = (index) => {
  const widths = ['92%', '78%', '88%', '70%'];

  return widths[(index - 1) % widths.length];
};

const animationIndex = ref(0);

const incrementAnimation = () => {
  if (animationIndex.value <= 4) {
    setTimeout(() => {
      animationIndex.value += 1;

      incrementAnimation();
    }, 0);
  }
};

onMounted(() => {
  incrementAnimation();
});
</script>

<style lang="less" scoped>
:deep(.v-skeleton-loader .v-skeleton-loader__heading),
:deep(.v-skeleton-loader .v-skeleton-loader__text) {
  background: @bg-canvas;
}

.flsa-skeleton-loader-container :deep(.v-skeleton-loader__heading) {
  width: 100%;
}

.flsa-skeleton-loader-container :deep(.v-skeleton-loader__text) {
  height: 20px;
  border-radius: 20px;
}

.flsa-skeleton-loader-container :deep(.v-skeleton-loader__paragraph) {
  .v-skeleton-loader__text:nth-child(2) {
    width: 100%;
    max-width: 100%;
  }

  .v-skeleton-loader__text:nth-child(3) {
    width: calc(100% - 75px);
    max-width: calc(100% - 75px);
  }
}

.flsa-skeleton-loader-container .skeleton-sub-heading :deep(.v-skeleton-loader__heading) {
  height: 20px;
}

.flsa-skeleton-loader-container .skeleton-nav-title :deep(.v-skeleton-loader__heading) {
  height: 28px;
}

.flsa-skeleton-loader-container .skeleton-nav-row :deep(.v-skeleton-loader__heading) {
  height: 24px;
}

.flsa-skeleton-loader-container .skeleton-section-title :deep(.v-skeleton-loader__heading) {
  height: 32px;
}

.flsa-skeleton-loader-container .skeleton-page-title :deep(.v-skeleton-loader__heading) {
  height: 36px;
}

.flsa-exemptions-heading-skel {
  margin-top: 12px;
  margin-bottom: 4px;
  padding-left: 16px;
}

.flsa-nav-child-skel {
  padding-left: 32px;
  margin-bottom: 4px;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.flsa-header-block {
  border-bottom: 1px solid @stroke-default;
}

.flsa-content-rail {
  max-width: 784px;
}

.flsa-right-sidebar-skel {
  min-height: 100%;
}

.flsa-right-sidebar-header {
  border-bottom: 1px solid @stroke-default;
}

.flsa-skeleton-loader-container .skeleton-right-title :deep(.v-skeleton-loader__heading) {
  height: 24px;
}

.hide {
  opacity: 0;
}

.show {
  animation: fadeIn 0.25s;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
</style>
